import type { ReactNode } from 'react';
import { useT } from '../i18n/locale-context';
import { LocaleLink } from '../i18n/locale-link';
import { localeCount } from '../lib/format';
import { ChevronLeft, ChevronRight } from './icons';

/**
 * Build a compact page range with ellipsis gaps, e.g. for page 5 of 12:
 *   [1, '…', 4, 5, 6, '…', 12]
 * Always shows the first and last page plus a window around the current page.
 */
function buildRange(current: number, total: number): Array<number | 'gap'> {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const pages = new Set<number>([1, total, current, current - 1, current + 1]);
  const sorted = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);

  const result: Array<number | 'gap'> = [];
  let prev = 0;
  for (const p of sorted) {
    if (p - prev > 1) result.push('gap');
    result.push(p);
    prev = p;
  }
  return result;
}

// 44px touch targets on mobile, tightening to the compact 32px pill at sm+.
const btnBase =
  'inline-flex h-11 min-w-11 items-center justify-center gap-1 rounded-pill border px-3 text-[13px] font-medium transition-colors sm:h-8 sm:min-w-8 sm:px-2.5 sm:text-[12.5px]';

export function Pagination({
  page,
  limit,
  total,
  shownTotal,
  onPageChange,
  hrefFor,
}: {
  /** 1-based current page. */
  page: number;
  /** Page size. */
  limit: number;
  /** Total item count across all pages. */
  total: number;
  /**
   * The number the «Показано … из N» caption prints when it must agree with a
   * count shown elsewhere on the page (the catalogue's live «N тендеров»),
   * while `total` keeps driving the page arithmetic. Defaults to `total`.
   */
  shownTotal?: number;
  onPageChange: (nextPage: number) => void;
  /**
   * Bare in-app path for a given page, e.g. `(2) => '/category/construction/page/2'`.
   * When supplied, every pager control renders as a real `<a href>` instead of a
   * `<button>`, which is the entire point: buttons are invisible to a crawler, so
   * before this existed the ONLY internally-linked tenders were the ~20 on each
   * hub's first page (measured 2026-08-01: 1,248 of 13,052 tender URLs reachable
   * by following links, against 1,695 pages indexed).
   *
   * Supply it ONLY for a canonical, unfiltered listing. A filtered view's state
   * lives in the query string, which these paths do not carry — emitting links
   * there would both drop the user's filters and mint crawlable URLs for every
   * filter permutation (index bloat). Callers fall back to `onPageChange` then.
   */
  hrefFor?: (page: number) => string;
}) {
  const t = useT();

  const totalPages = Math.max(1, Math.ceil(total / limit));
  // Don't render a pager when there's a single page (or nothing) to show.
  if (total <= 0 || totalPages <= 1) return null;

  const clamped = Math.min(Math.max(page, 1), totalPages);
  const from = (clamped - 1) * limit + 1;
  const to = Math.min(clamped * limit, total);
  const range = buildRange(clamped, totalPages);

  // Paging is a query-string change, so useScrollResetOnNavigate (keyed on
  // pathname) doesn't fire — the reader would be left at the bottom, where the
  // pager is, and have to scroll up to see the new page. A new PAGE of results
  // should start at the top (unlike an in-place filter tweak). The shell scrolls
  // `<main>`, not the window; reset both, belt-and-braces.
  const changePage = (next: number): void => {
    onPageChange(next);
    document.querySelector('main')?.scrollTo({ top: 0 });
    window.scrollTo({ top: 0 });
  };

  /**
   * One pager control. Renders an anchor when `hrefFor` is supplied (crawlable,
   * and real middle-click / open-in-new-tab behaviour), a button otherwise.
   * Disabled controls stay buttons in both modes — there is no page 0 or
   * page totalPages+1 to link to, and an <a> with no href is not focusable.
   */
  const Control = ({
    target,
    disabled,
    current,
    ariaLabel,
    className,
    children,
  }: {
    target: number;
    disabled?: boolean;
    current?: boolean;
    ariaLabel?: string;
    className: string;
    children: ReactNode;
  }) => {
    if (hrefFor && !disabled) {
      return (
        <LocaleLink
          to={hrefFor(target)}
          aria-label={ariaLabel}
          aria-current={current ? 'page' : undefined}
          className={className}
        >
          {children}
        </LocaleLink>
      );
    }
    return (
      <button
        type="button"
        onClick={() => changePage(target)}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-current={current ? 'page' : undefined}
        className={className}
      >
        {children}
      </button>
    );
  };

  return (
    <nav
      aria-label="Pagination"
      className="mt-5 flex flex-col items-center justify-between gap-3 sm:flex-row"
    >
      <span className="text-[12.5px] text-ink-muted">
        {/* Grouped where the deployment has a `numberLocale` (Spain: «de 22.028»,
            as the page's own counts print it); UZ/KZ interpolate the bare number. */}
        {t('pagination.showing', {
          from: localeCount(from),
          to: localeCount(to),
          total: localeCount(shownTotal ?? total),
        })}
      </span>

      <div className="flex items-center gap-1.5">
        <Control
          target={clamped - 1}
          disabled={clamped <= 1}
          ariaLabel={t('pagination.prev')}
          className={`${btnBase} border-edge bg-bone-surface text-ink-soft hover:border-brand hover:bg-brand-dim hover:text-brand disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-edge disabled:hover:bg-bone-surface disabled:hover:text-ink-soft`}
        >
          <ChevronLeft className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">{t('pagination.prev')}</span>
        </Control>

        {/* Numbered pages: desktop only — they'd overflow a 320px row. */}
        <div className="hidden items-center gap-1.5 sm:flex">
          {range.map((item, i) =>
            item === 'gap' ? (
              <span
                key={`gap-${i}`}
                className="inline-flex h-8 min-w-6 items-center justify-center text-[12.5px] text-ink-muted"
              >
                …
              </span>
            ) : (
              <Control
                key={item}
                target={item}
                current={item === clamped}
                className={`${btnBase} ${
                  item === clamped
                    ? 'border-brand bg-brand text-white'
                    : 'border-edge bg-bone-surface text-ink-soft hover:border-brand hover:bg-brand-dim hover:text-brand'
                }`}
              >
                {item}
              </Control>
            ),
          )}
        </div>

        {/* Compact position indicator — mobile stand-in for the number strip. */}
        <span
          aria-hidden="true"
          className="px-1 text-[13px] font-semibold tabular-nums text-ink sm:hidden"
        >
          {clamped} / {totalPages}
        </span>

        <Control
          target={clamped + 1}
          disabled={clamped >= totalPages}
          ariaLabel={t('pagination.next')}
          className={`${btnBase} border-edge bg-bone-surface text-ink-soft hover:border-brand hover:bg-brand-dim hover:text-brand disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-edge disabled:hover:bg-bone-surface disabled:hover:text-ink-soft`}
        >
          <span className="hidden sm:inline">{t('pagination.next')}</span>
          <ChevronRight className="h-3.5 w-3.5" />
        </Control>
      </div>
    </nav>
  );
}
